import React from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

interface ConfirmDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    isDestructive?: boolean;
    variant?: 'warning' | 'danger' | 'success';
    isLoading?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    isDestructive = false,
    variant,
    isLoading = false
}) => {
    if (!isOpen) return null;

    const currentVariant = variant || (isDestructive ? 'danger' : 'warning');

    const colors = {
        warning: {
            bg: 'bg-orange-500/10',
            text: 'text-orange-500',
            button: 'bg-orange-600 hover:bg-orange-500 shadow-orange-900/20'
        },
        danger: {
            bg: 'bg-red-500/10',
            text: 'text-red-500',
            button: 'bg-red-600 hover:bg-red-500 shadow-red-900/20'
        },
        success: {
            bg: 'bg-green-500/10',
            text: 'text-green-500',
            button: 'bg-green-600 hover:bg-green-500 shadow-green-900/20'
        }
    };

    const config = colors[currentVariant];

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-sm shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden">
                <div className="p-10 text-center">
                    <div className={`w-20 h-20 ${config.bg} rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm`}>
                        <AlertTriangle className={`w-10 h-10 ${config.text}`} />
                    </div>
                    <h2 className="text-2xl font-black text-slate-800 tracking-tight mb-3">{title}</h2>
                    <p className="text-sm font-bold text-slate-500 leading-relaxed px-2">
                        {message}
                    </p>
                </div>
                <div className="px-8 py-6 bg-slate-50/50 border-t border-slate-100 flex gap-4">
                    <button
                        onClick={onClose}
                        disabled={isLoading}
                        className="flex-1 py-3.5 border-2 border-slate-200 rounded-2xl text-[12px] font-black uppercase tracking-widest text-slate-400 hover:bg-white hover:border-slate-300 hover:text-slate-600 transition-all duration-300 disabled:opacity-50"
                    >
                        {cancelText}
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isLoading}
                        className={`flex-1 py-3.5 ${config.button} text-white rounded-2xl text-[12px] font-black uppercase tracking-widest transition-all duration-300 shadow-xl flex justify-center items-center gap-2 disabled:opacity-50 active:transform active:scale-95`}
                    >
                        {isLoading ? <Loader2 className="animate-spin w-4 h-4" /> : confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmDialog;
